import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Globe, TrendingUp, Mail, Phone, MapPin, Linkedin, Twitter } from 'lucide-react';

// Footer Component
export const Footer = () => {
  const navigate = useNavigate();

  const legalLinks = [
    { label: 'Terms & Conditions', path: '/terms-and-conditions' },
    { label: 'Privacy Policy', path: '/privacy-policy' },
    { label: 'Refund & Cancellation', path: '/terms-and-conditions#refunds' },
    { label: 'Cookie Policy', path: '/privacy-policy#cookies' }
  ];

  const footerSections = [
    {
      title: 'Product',
      links: ['Credit Scores', 'Digital Onboarding', 'ERP Integration', 'Analytics']
    },
    {
      title: 'Solutions',
      links: ['For Manufacturers', 'For Traders', 'For Distributors', 'Use Cases']
    },
    {
      title: 'Company',
      links: ['About Us', 'Security', 'Pricing', 'Contact']
    },
    {
      title: 'Resources',
      links: ['FAQ', 'Documentation', 'Blog', 'Support']
    }
  ];

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Space+Mono:wght@400;700&family=Syne:wght@400;500;600;700;800&display=swap');
        
        * {
          font-family: 'Syne', sans-serif;
        }
        
        .mono {
          font-family: 'Space Mono', monospace;
        }
        
        .glass {
          background: rgba(255, 255, 255, 0.03);
          backdrop-filter: blur(20px);
        }
      `}</style>
      
      <footer className="relative bg-slate-950 text-white border-t border-white/10 overflow-hidden">
        {/* Background Effects */}
        <div className="absolute inset-0 opacity-30 pointer-events-none">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-blue-500/20 rounded-full filter blur-3xl"></div>
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-purple-500/20 rounded-full filter blur-3xl"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-6 py-16">
          {/* Main Footer Content */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-12 mb-12">
            {/* Brand Section */}
            <div className="lg:col-span-2">
              <div className="flex items-center space-x-3 mb-6">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-600 to-purple-600 flex items-center justify-center shadow-lg">
                  <TrendingUp className="w-6 h-6 text-white" />
                </div>
                <div>
                  <div className="text-xl font-bold">PCRA</div>
                  <div className="text-xs text-slate-400 mono">TECHNOLOGIES</div>
                </div>
              </div>
              <p className="text-slate-400 mb-6 leading-relaxed">
                Credit & Collection Intelligence for the pharmaceutical industry. Making B2B credit decisions data-driven and intelligent.
              </p>
              <div className="flex space-x-3">
                <a href="#" className="w-10 h-10 glass rounded-lg flex items-center justify-center hover:bg-blue-600 transition-all duration-300 group">
                  <Linkedin className="w-5 h-5 text-slate-400 group-hover:text-white" />
                </a>
                <a href="#" className="w-10 h-10 glass rounded-lg flex items-center justify-center hover:bg-blue-600 transition-all duration-300 group">
                  <Twitter className="w-5 h-5 text-slate-400 group-hover:text-white" />
                </a>
                <a href="#" className="w-10 h-10 glass rounded-lg flex items-center justify-center hover:bg-blue-600 transition-all duration-300 group">
                  <Globe className="w-5 h-5 text-slate-400 group-hover:text-white" />
                </a>
              </div>
            </div>

            {/* Footer Links */}
            {footerSections.map((section) => (
              <div key={section.title}>
                <h3 className="text-white font-bold mb-4 text-sm">{section.title}</h3>
                <ul className="space-y-3">
                  {section.links.map((link) => (
                    <li key={link}>
                      <button className="text-slate-400 hover:text-white transition-colors text-sm">
                        {link}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          {/* Contact Section */}
          <div className="glass rounded-2xl p-8 mb-12">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="flex items-start space-x-3">
                <div className="w-10 h-10 rounded-lg bg-blue-600/20 flex items-center justify-center flex-shrink-0">
                  <Mail className="w-5 h-5 text-blue-400" />
                </div>
                <div>
                  <div className="text-xs text-slate-400 mb-1">Mail</div>
                  <div className="text-sm text-white">pcratechno@gmail.com</div>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <div className="w-10 h-10 rounded-lg bg-purple-600/20 flex items-center justify-center flex-shrink-0">
                  <Phone className="w-5 h-5 text-purple-400" />
                </div>
                <div>
                  <div className="text-xs text-slate-400 mb-1">Phone</div>
                  <div className="text-sm text-white">+91 7036222635</div>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                {/* <div className="w-10 h-10 rounded-lg bg-cyan-600/20 flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-5 h-5 text-cyan-400" />
                </div> */}
                 <MapPin className="w-5 h-5 text-cyan-400" />
                <div>
                  <div className="text-xs text-slate-400 mb-1">Headquarters</div>
                  <div className="text-sm text-white">Hyderabad,Telangana,India</div>
                </div>
              </div>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="pt-8 border-t border-white/10">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4">
              <div className="flex items-center gap-2 text-sm text-slate-400">
                <div className="w-2 h-2 rounded-full bg-blue-500 animate-pulse"></div>
                <span className="mono">© 2025 PCRA Technologies • All rights reserved</span>
              </div>
              <div className="flex flex-wrap gap-6 text-sm text-slate-400">
                {legalLinks.map((link) => (
                  <button key={link.label} onClick={() => navigate(link.path)} className="hover:text-white transition-colors">
                    {link.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </footer>
    </>
  );
};

// Demo Component
export default function FooterDemo() {
  return (
    <div className="">
      <Footer />
    </div>
  );
}